import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, EMPTY, forkJoin, Observable, of, Subscription } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { FieldType, FilterName, PageEvent, SortEvent, SearchEvent, FilterEvent, DefaultPage, TableConfig, ColumnSorterInfo, ConfigSettingType, ConfigScope, FormatType } from 'src/app/models/custom-table/custom-table.model';
import { MenuDTO, RouteData } from 'src/app/models/Menu/MenuDTO';
import { ProductDTO, SiteProductDTO } from 'src/app/models/Products/productDTO';
import { SnackbarType } from 'src/app/models/snackbar/snackbar.model';
import { ConfirmationDialogComponent } from 'src/app/shared/modules/dialogs/confirmation-dialog/confirmation-dialog.component';
import { EditRowDataDialogComponent } from 'src/app/shared/modules/dialogs/edit-row-data-dialog/edit-row-data-dialog.component';
import { SelectProductsDialogComponent } from 'src/app/shared/modules/dialogs/select-products-dialog/select-products-dialog.component';
import { ProductService } from 'src/app/shared/services/products/products/product.service';
import { SitesService } from 'src/app/shared/services/sites/sites.service';
import { TableConfigService } from 'src/app/shared/services/table-config/table-config.service';
import { subMenusIterator } from 'src/app/shared/utils/menuUtils';
import { SnackBarUtils } from 'src/app/shared/utils/snackbarUtils';
import { setSnackbar } from 'src/app/store/actions/messages/message.actions';
import { AppState } from 'src/app/store/reducers';
import { loadedMenusState } from 'src/app/store/reducers';
import { FailedProductsDialogComponent } from './dialog/failed-products-dialog/failed-products-dialog.component';

const USAGE_CODE = "SITE_PRODUCTS";

interface SaveResult {
  successfulProducts: string [];
  failedToRemove: { productCode: string; code: string}[];
}

@Component({
  selector: "app-store-site-products",
  templateUrl: "./store-site-products.component.html",
  styleUrls: ["./store-site-products.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class StoreSiteProductsComponent implements OnInit {
  @Input() siteCode: string;
  @Input() canWrite: string;

  // menu
  menus$: Observable<MenuDTO[]>;
  menuSub: Subscription;
  tableTitle: string;
  headerIcon: string;

  // subscriptions
  public siteProductsSub: Subscription;

  columnInfo$: Observable<ColumnSorterInfo[]>;
  totalProductsStore$ = new BehaviorSubject<number>(0);
  public isLoading$ = new BehaviorSubject<boolean>(false);

  siteProductsList: SiteProductDTO[] = [];

  private lastTableEvent: any;

  isNotFuelFilter = {
    column: "isFuel",
    fieldType: FieldType.BOOLEAN,
    operator: FilterName.EQUAL,
    value: false,
  };

  tableConfig: TableConfig[] = [
    {
      header: "CustomGrid.Options",
      field: FieldType.OPTIONS,
      fieldType: FieldType.OPTIONS,
    },
    {
      header: "PosMovements.productCode",
      field: "productCode",
      fieldType: FieldType.STRING,
      identifier: true,
    },
    {
      header: "PosMovements.description",
      field: "productDescription",
      fieldType: FieldType.STRING,
      searchable: true,
    },
    {
      header: "SiteManagement.siteProducts.referencePrice",
      field: "unitPriceGrossReference",
      fieldType: FieldType.NUMBER,
      formatType: FormatType.AMOUNT,
    },
    {
      header: "SiteManagement.siteProducts.storePrice",
      field: "unitPriceGross",
      fieldType: FieldType.NUMBER,
      formatType: FormatType.AMOUNT,
    },
    {
      header: "SiteManagement.siteProducts.favorite",
      field: "favorite",
      fieldType: FieldType.BOOLEAN,
    },
    {
      header: "SiteManagement.siteProducts.availableInStore",
      field: "enabled",
      fieldType: FieldType.BOOLEAN,
    },
    {
      header: "SiteManagement.siteProducts.productGroupCode",
      field: "productGroupCode",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Products.token",
      field: "token",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Products.productFamilyCode",
      field: "productFamilyCode",
      fieldType: FieldType.STRING,
      hidden: true,
    },
  ];

  constructor(
    private store: Store<AppState>,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private titleService: Title,
    private translate: TranslateService,
    private sitesService: SitesService,
    private productService: ProductService,
    private tableConfigService: TableConfigService
  ) {}

  ngOnInit(): void {
    this.menus$ = this.store.pipe(select(loadedMenusState));
    this.loadScreenTitle();
    this.loadProductsSite();
  }

  loadScreenTitle() {
    this.menuSub = this.menus$.subscribe((menus: MenuDTO[]) => {
      if (menus && menus.length) {
        this.route.data.subscribe((routeData: RouteData) => {
          menus.forEach((menuEl: MenuDTO) => {
            const selectedMenu = subMenusIterator(menuEl, routeData);
            if (selectedMenu) {
              this.headerIcon = selectedMenu.icon;
              this.tableTitle = selectedMenu.description;
              this.titleService.setTitle(this.tableTitle);
            }
          });
        });
      }
    });
  }

  loadProductsSite(
    pageEvent?: PageEvent,
    sortEvent?: SortEvent,
    searchEvent?: SearchEvent,
    filterEvent?: FilterEvent
  ): void {
    const { pageIndex, pageSize } = pageEvent || {};
    this.isLoading$.next(true);

    this.siteProductsSub = this.sitesService
      .getSiteStoreProductListPagination(
        this.siteCode,
        {
          skip: (pageIndex * pageSize) | DefaultPage.PAGE_INDEX,
          top: pageSize ?? DefaultPage.PAGE_SIZE,
          realSize: true,
        },
        searchEvent,
        sortEvent,
        filterEvent,
        false
      )
      .pipe(
        map((result: any) => {
          const items = result?.result?.items ?? result?.result ?? [];
          const size = result?.result?.size
          this.totalProductsStore$.next(size);

          const mappedProductList = (items as SiteProductDTO[]).map(
            (siteProducts: SiteProductDTO) => {
              return {
                ...siteProducts,
                code: siteProducts.productCode,
                productCode: siteProducts.productCode,
                productDescription: siteProducts.productDescription,
                unitPriceGross: siteProducts.unitPriceGross,
                productPrice: siteProducts.unitPriceGross,
                availableInStore: true,
                token: siteProducts.token,
                favorite: siteProducts.favorite,
              } as SiteProductDTO;
            }
          );

          return mappedProductList;
        }),
        catchError((error) => {
          this.store.dispatch(
            setSnackbar(
              SnackBarUtils.setSnackBar(
                this.translate.instant("SiteManagement.siteProducts.loadSiteProductsError"),
                SnackbarType.ERROR
              )
            )
          );
          return EMPTY;
        }),
        finalize(() => this.isLoading$.next(false))
      )
      .subscribe((result: SiteProductDTO[]) => {
        this.siteProductsList = result;
      });
  }

  // Helper to fetch all products without pagination
  private getAllSiteProducts(): Observable<SiteProductDTO[]> {
    return this.sitesService
      .getSiteStoreProductListPagination(
        this.siteCode,
        {
          skip: 0,
          top: DefaultPage.EXCEL_PAGE_SIZE,
          realSize: true,
        }, undefined, undefined, undefined, false)
      .pipe(
        map((result: any) => result?.result?.items ?? result?.result ?? [])
      );
  }

  public openSearchProductsDialog(): void {
    this.isLoading$.next(true);

    this.getAllSiteProducts()
      .pipe(finalize(() => this.isLoading$.next(false)))
      .subscribe((allProducts) => {
      
        const preselectedProducts = allProducts.map((p) => ({
          ...p,
          code: p.productCode,
        }));

        const dialogRef = this.dialog.open(SelectProductsDialogComponent, {
          width: "1400px",
          height: "80vh",
          panelClass: "search-customer-dialog",
          autoFocus: false,
          data: {
            preselectedProducts,
            advancedFilter: [this.isNotFuelFilter],
          },
        });

        dialogRef.afterClosed().subscribe((selectedProducts: ProductDTO[]) => {
          if (!selectedProducts) return;

          const productListPayload = selectedProducts.map((p) => {
            const code = p.code;
            
            const currentProducts = preselectedProducts.find(
              (storeProd) => storeProd.productCode === code
            );
            
            if (currentProducts) {
              return {
                productCode: code,
                siteCode: this.siteCode,
                unitPriceGross: Number(currentProducts.unitPriceGross),
                enabled:
                  currentProducts.enabled !== undefined
                    ? !!currentProducts.enabled
                    : true,
                favorite: false,
                productDescription: currentProducts.productDescription,
                shouldBeLinked: p.shouldBeLinked
              };
            } else {
              return {
                productCode: code,
                siteCode: this.siteCode,
                unitPriceGross: Number(p.unitPriceGross),
                enabled: true,
                favorite: false,
                productDescription: p.description,
                shouldBeLinked: p.shouldBeLinked
              };
            }
          });

          this.saveProductList(productListPayload, false);
        });
      });
  }

  //Fetch errors messages to show as a Warning List (FailedProductsDialog)
  private parseUpdateProductList(response: any, payload: SiteProductDTO[]): SaveResult {
    const errors = response?.result?.errors ?? {};

    const successfulProducts: string[] = []
    const failedToRemove: { productCode: string; code: string}[] = [];
    
    payload.forEach((item) => {
      const itemErrors = errors[item.productCode];

      if(itemErrors) {
        const errorMessages = itemErrors.map((e:any) => e.code).join(' ');
        failedToRemove.push({ productCode: item.productCode, code: errorMessages});
      } else {
        successfulProducts.push(item.productCode)
      }
    });

    return { successfulProducts, failedToRemove}
  }

  private saveProductList(payload: any[], isEdit: boolean) {
    this.isLoading$.next(true);
    const request$ = isEdit 
    ? this.sitesService
      .createSiteStoreProductList({
        siteCode: this.siteCode,
        productList: payload,
      }) 
    :  this.sitesService
      .updateSiteStoreProductList({
        siteCode: this.siteCode,
        productList: payload,
      });
      
      request$
      .pipe(
        map((response) => this.parseUpdateProductList(response, payload)),
        tap(() => {
          
          if (this.lastTableEvent) {
            const { pageEvent, sortEvent, searchEvent, filterEvent } =
              this.lastTableEvent;
            this.loadProductsSite(
              pageEvent,
              sortEvent,
              searchEvent,
              filterEvent
            );
          } else {
            this.loadProductsSite();
          }
        }),
        catchError((err) => {
          this.store.dispatch(
            setSnackbar(
              SnackBarUtils.setSnackBar(
                this.translate.instant(
                  "SiteManagement.siteProducts.saveProductError"
                ),
                SnackbarType.ERROR
              )
            )
          );
          return EMPTY;
        }),
        finalize(() => this.isLoading$.next(false))
      )
      .subscribe((result)=> {
        if(result.failedToRemove.length === 0){
          this.store.dispatch(
            setSnackbar(
              SnackBarUtils.setSnackBar(
                this.translate.instant(
                  "SiteManagement.siteProducts.saveProductSuccess"
                ),
                SnackbarType.SUCCESS
              )
            )
          );
        } else {
          this.openFailedToLinkProductsDialog(result);
        }
      });
  }

  openFailedToLinkProductsDialog(result: SaveResult): void {
    const dialogData = {
      width: '900px',
      panelClass: 'failed-link-products-dialog',
      autoFocus:false,
      data: {
         failedProducts: result.failedToRemove
      }
    }

    this.dialog.open(FailedProductsDialogComponent, dialogData);
  }

  onGetTableData(tableEvent: any) {
    this.lastTableEvent = tableEvent;
    
    const { pageEvent, sortEvent, searchEvent, filterEvent } = tableEvent;
    this.loadProductsSite(pageEvent, sortEvent, searchEvent, filterEvent);
  }

  onSaveTableConfig(tableColumnInfo: ColumnSorterInfo[]) {
    this.isLoading$.next(true);
    this.tableConfigService
      .saveConfigSettings({
        code: USAGE_CODE,
        settingTypeCode: ConfigSettingType.TABLE_CONFIG,
        scope: ConfigScope.USER,
        value: { tableColumnInfo },
      })
      .pipe(
        tap(() =>
          this.store.dispatch(
            setSnackbar(
              SnackBarUtils.setSnackBar(
                this.translate.instant("TableConfig.SaveSuccess"),
                SnackbarType.SUCCESS
              )
            )
          )
        ),
        catchError((error) => {
          this.store.dispatch(
            setSnackbar(
              SnackBarUtils.setSnackBar(
                this.translate.instant("TableConfig.SaveError"),
                SnackbarType.ERROR
              )
            )
          );
          return EMPTY;
        }),
        finalize(() => this.isLoading$.next(false))
      )
      .subscribe();
  }
}
